import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Icon from './Icon';
import { PRIMARY } from './CaptureSheets';
import { useApp } from '../lib/AppContext';
import { rememberMode } from '../lib/mode';

const PLACEHOLDER = 'Bạn mới'; // User::PLACEHOLDER_NAME
const CHOICES = [
  { id: 'self', icon: 'user', title: 'Tự theo dõi', text: 'Ghi bữa ăn, buổi tập, lên thực đơn cho bản thân.' },
  { id: 'coach', icon: 'users', title: 'Tôi là huấn luyện viên', text: 'Theo dõi học viên, đặt mục tiêu, hẹn lịch tập.', dark: true },
];
// Someone who came through a coach's invite or code is a student: they go straight to it, never asked.
const JOINING = /^\/(me\/)?(i|c)\//;

// O2 "Bạn dùng Macro Mate để làm gì?": asked once, right after a new account first signs in. Self carries on to
// the onboarding (U1-U3, Onboarding); coach turns coach mode on, opening the app there, and goes to the coach's
// onboarding (O3, O4); the personal onboarding waits for their first visit to the personal app. Leaving without
// answering asks again next time.
export default function RolePicker({ base }) {
  const { api, profile, setProfile, setCoach } = useApp();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!profile?.needsRole || JOINING.test(pathname)) return null;

  const firstName = profile.name && profile.name !== PLACEHOLDER ? profile.name.trim().split(/\s+/).pop() : null;
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await api.post('/signup_intent', { intent: choice });
      if (data.coach) {
        setCoach(data.coach);
        rememberMode('coach');
        navigate(`${base}/coach/start`, { replace: true });
      }
      setProfile((p) => ({ ...p, needsRole: false }));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div data-theme="macro" role="dialog" aria-modal="true" aria-labelledby="role-title"
      className="fixed inset-0 z-50 overflow-y-auto bg-base-200 text-base-content">
      <div className="pt-safe pb-safe mx-auto flex min-h-dvh max-w-xl flex-col gap-4 px-4 pb-4">
        <div className="pt-10">
          <p className="text-sm font-semibold text-muted">Chào {firstName || 'bạn'} 👋</p>
          <h1 id="role-title" className="text-[28px] font-extrabold leading-tight">Bạn dùng Macro Mate để làm gì?</h1>
        </div>
        <div role="radiogroup" aria-labelledby="role-title" className="flex flex-col gap-3">
          {CHOICES.map((c) => {
            const on = choice === c.id;
            return (
              <button key={c.id} type="button" role="radio" aria-checked={on} onClick={() => setChoice(c.id)}
                className={`flex min-h-20 w-full items-center gap-3 rounded-[20px] border-2 bg-base-100 p-4 text-left ${on ? 'border-[#17804f]' : 'border-transparent'}`}>
                <span className={`grid size-11 shrink-0 place-items-center rounded-[14px] ${c.dark ? 'bg-[#15261f] text-[#6fd3a0]' : 'bg-[#e3f1e9] text-[#17804f]'}`}>
                  <Icon name={c.icon} className="size-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-bold">{c.title}</span>
                  <span className="block text-sm text-muted">{c.text}</span>
                </span>
                <span className={`grid size-6 shrink-0 place-items-center rounded-full border-2 ${on ? 'border-[#17804f]' : 'border-line'}`}>
                  {on && <span className="size-3 rounded-full bg-[#17804f]" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-3 rounded-[20px] bg-base-100 p-4">
          <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-[#fdf3d7] text-[#8a6100]">
            <Icon name="refresh" className="size-5" />
          </span>
          <span>
            <span className="block text-sm font-bold">Chuyển qua lại bất cứ lúc nào</span>
            <span className="block text-sm text-muted">
              HLV vẫn tự theo dõi được bản thân, và người tự theo dõi có thể bật chế độ HLV sau. Một tài khoản, không cần đăng xuất.
            </span>
          </span>
        </div>
        <div className="mt-auto flex flex-col gap-2 pt-4">
          {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
          <button type="button" className={PRIMARY} disabled={!choice || busy} onClick={submit}>
            {busy && <span className="loading loading-spinner loading-sm" />} Tiếp tục
          </button>
        </div>
      </div>
    </div>
  );
}
